/**
 * Queries and mutations of the `alerts` slice. Every key lives under the
 * `"frontend"` prefix, so the SSE `change` the engine emits when it records
 * a firing refetches the log, the summary and the rules together.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deleteJson, fetchJson, paths, postJson } from "@/api/client";
import { qk } from "@/api/keys";
import { useApi } from "@/api/hooks";
import type {
  AlertRow,
  AlertsQuery,
  AlertsSummary,
  Deleted,
  PreviewRequest,
  PreviewResponse,
  PushKey,
  PushDelivery,
  PushSubscriptionInfo,
  PushTest,
  Rule,
  RulesQuery,
  RuleWrite,
} from "@/api/types";

function invalidateAlerts(qc: ReturnType<typeof useQueryClient>) {
  return qc.invalidateQueries({ queryKey: ["frontend"] });
}

export function useRules(query: RulesQuery = {}) {
  return useApi<Rule[]>(qk.rules(query), paths.rules(), { ...query });
}

export function useAlertLog(query: AlertsQuery = {}) {
  return useApi<AlertRow[]>(qk.alertLog(query), paths.alerts(), { ...query });
}

/**
 * The bell. Polled slowly as a backstop: the engine's `change` event is
 * what normally refreshes it, and this only covers a dropped stream.
 */
export function useAlertsSummary() {
  return useApi<AlertsSummary>(qk.alertsSummary(), paths.alertsSummary(), undefined, {
    staleTime: 15_000,
    refetchInterval: 60_000,
    silent: true,
  });
}

export function useSaveRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rule: RuleWrite) => postJson<Rule>(paths.rules(), rule),
    onSuccess: () => invalidateAlerts(qc),
  });
}

export function useDeleteRule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteJson<Deleted>(paths.rule(id)),
    onSuccess: () => invalidateAlerts(qc),
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id?: number) =>
      postJson<AlertsSummary>(paths.alertsRead(), {}, id === undefined ? undefined : { id }),
    onSuccess: () => invalidateAlerts(qc),
  });
}

/**
 * "How noisy would this be?" — the same evaluator over stored history.
 * A mutation rather than a query: it is asked for explicitly, about a rule
 * that may not exist yet.
 */
export function usePreview() {
  return useMutation({
    mutationFn: (request: PreviewRequest) => postJson<PreviewResponse>(paths.rulePreview(), request),
  });
}

export function usePushKey() {
  return useQuery({
    queryKey: qk.pushKey(),
    queryFn: () => fetchJson<PushKey>(paths.pushKey()),
    staleTime: 5 * 60_000,
  });
}

export function usePushSubscriptions() {
  return useApi<PushSubscriptionInfo[]>(qk.pushSubscriptions(), paths.pushSubscriptions());
}

export function useTestPush() {
  return useMutation({
    mutationFn: (force?: boolean) =>
      postJson<PushTest>(paths.pushTest(), {}, force ? { force: true } : undefined),
  });
}

export function useAlertDeliveries(id: number) {
  return useApi<PushDelivery[]>(["frontend", "alert-deliveries", id], paths.alertDeliveries(id), undefined, {
    refetchInterval: 5000,
  });
}
